export const SETTINGS_STORAGE_KEY = 'prompt_catalyst_settings';

// Check if it's first time use and store initial settings
export const initializeFirstTimeSettings = () => {
  try {
    // Check if settings exist in localStorage
    if (!localStorage.getItem(SETTINGS_STORAGE_KEY)) {
      console.log('First time user detected, applying default settings');
      // Store default settings for first time users
      // This will include our default style (realism) in the styles array
      saveSettings(defaultSettings);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error initializing settings:', error);
    return false;
  }
};

// Initial default settings used when the app is first loaded
export const defaultSettings = {
  // Image settings
  style: 'realism', // Default to photorealism for new users
  lighting: 'not_specified',
  cameraAngle: 'not_specified',
  model: 'not_specified',
  purpose: 'not_specified', // Default to portrait for new users
  promptLength: 2,
  creativity: 5,
  promptAmount: 3, // Default to 3 prompts for free users
  midjourneyParams: [],
  
  // Multi-select settings - initialize consistently with the single-value settings above
  styles: ['realism'], // Initialize with Photorealism selected
  lightingEffects: [], // Array of selected lighting effects
  cameraAngles: [], // Array of selected camera angles
  
  // Video settings
  videoStyle: 'not_specified',
  cameraMovement: 'not_specified',
  // Video mode uses the same promptLength, cameraAngle, and lighting settings as image mode
};

// Rest of the file remains the same...
export const getPromptLengthLabel = (value) => {
  const numericValue = parseInt(value);
  switch(numericValue) {
    case 1: return 'Short';
    case 2: return 'Medium';
    case 3: return 'Long';
    default: return 'Medium';
  }
};

export const loadSettings = () => {
  try {
    const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      // Merge with default settings to ensure all required fields exist
      return { ...defaultSettings, ...parsedSettings };
    }
  } catch (error) {
    console.error('Error loading settings:', error);
  }
  return defaultSettings;
};

export const saveSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving settings:', error);
  }
};

export const preparePromptSettings = (settings) => {
  const prepared = {};

  if (settings.isVideoMode) {
    // Video mode settings
    if (settings.videoStyle !== 'not_specified') prepared.videoStyle = settings.videoStyle;
    if (settings.cameraMovement !== 'not_specified') prepared.cameraMovement = settings.cameraMovement;
  } else {
    // Image mode settings
    if (settings.style !== 'not_specified') prepared.style = settings.style;
    
    // Always include styles - prioritize multi-select array over single value
    if (settings.styles && settings.styles.length > 0) {
    prepared.styles = settings.styles;
    } else if (settings.style !== 'not_specified') {
    // If no styles array but a style is specified, create a styles array with that value
    prepared.styles = [settings.style];
  }
    
    if (settings.model !== 'not_specified') prepared.model = settings.model;
    if (settings.purpose !== 'not_specified') prepared.purpose = settings.purpose;
  }

  // Shared settings between video and image modes
  if (settings.lighting !== 'not_specified') prepared.lighting = settings.lighting;
  
  // Always include lighting effects - prioritize multi-select array over single value
  if (settings.lightingEffects && settings.lightingEffects.length > 0) {
    prepared.lightingEffects = settings.lightingEffects;
  } else if (settings.lighting !== 'not_specified') {
    // If no lightingEffects array but lighting is specified, create an array with that value
    prepared.lightingEffects = [settings.lighting];
  }
  
  if (settings.cameraAngle !== 'not_specified') prepared.cameraAngle = settings.cameraAngle;
  
  // Always include camera angles - prioritize multi-select array over single value
  if (settings.cameraAngles && settings.cameraAngles.length > 0) {
    prepared.cameraAngles = settings.cameraAngles;
  } else if (settings.cameraAngle !== 'not_specified') {
    // If no cameraAngles array but cameraAngle is specified, create an array with that value
    prepared.cameraAngles = [settings.cameraAngle];
  }
  
  // Always include prompt length
  prepared.promptLength = getPromptLengthLabel(settings.promptLength);

  // Add Midjourney parameters if they exist (image mode only)
  if (!settings.isVideoMode && settings.model === 'midjourney' && settings.midjourneyParams?.length > 0) {
    prepared.midjourneyParams = settings.midjourneyParams;
  }
  
  // Always include creativity setting
  prepared.creativity = settings.creativity;

  // Include prompt amount if defined
  prepared.promptAmount = settings.promptAmount || 1;

  return prepared;
};